import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { ModalService } from '@app/core/services/modal.service';
import { GotoParms, StateService } from '@app/core/services/state.service';
import { UtilityService } from '@app/core/services/utility.service';
import { OrderHeadGet, OrderheadService, ScanConsignmentResponse } from '@app/services/orderhead.service';
import { firstValueFrom } from 'rxjs';
import { OrderHeadCancelDialogComponent, OrderHeadCancelDialogResult } from '../order-head-cancel-dialog/order-head-cancel-dialog.component';

@Component({
    selector: 'tt-order-head-functions',
    templateUrl: './order-head-functions.component.html',
    styleUrls: ['./order-head-functions.component.css'],
})
export class OrderHeadFunctionsComponent {
    @Input()
    public get?: OrderHeadGet;

    @Input()
    public orderHeadProvider?: () => OrderHeadGet | undefined;

    @Input()
    public showDynamicSpecialSave = false;

    @Input()
    public dynamicSpecialSaving = false;

    @Output()
    public dynamicSpecialSaveRequested = new EventEmitter<void>();

    @Output()
    public converted = new EventEmitter<void>();

    @Output()
    public refreshRequested = new EventEmitter<void>();

    public creatingConsignment = false;

    public creatingSveaPaymentLink = false;

    public pushingOrder = false;

    public cancelling = false;

    public deleting = false;

    constructor(
        private orderhead: OrderheadService,
        private modal: ModalService,
        private state: StateService,
        private utility: UtilityService,
        private dialog: MatDialog,
    ) {}

    public async createConsignment(event?: MouseEvent) {
        if (!this.get || this.creatingConsignment) return;

        try {
            this.creatingConsignment = true;

            const response = await this.orderhead.scanConsignment({ barcode: `oi${this.get.order_internal_no}` });

            if (this.hasError(response)) {
                await this.showWarning(response.errormessage ?? '');
                return;
            }

            this.goTo(response, event);
        } catch (error) {
            console.error(error);
        } finally {
            this.creatingConsignment = false;
        }
    }

    public async createSveaPaymentLink() {
        if (!this.get || this.creatingSveaPaymentLink) return;

        try {
            this.creatingSveaPaymentLink = true;

            const response = await this.orderhead.createSveaPaymentLink({ order_internal_no: this.get.order_internal_no });

            await this.modal.show({
                type: response.errorcode === '0' ? 'success' : 'danger',
                title: 'tt_svea_payment_modal_title',
                translateTitle: true,
                message: response.errormessage,
                buttons: [{ label: 'tt_svea_payment_modal_confirm', cssClass: '', close: true, type: response.errorcode === '0' ? 'success' : 'danger' }],
            });

            this.refreshRequested.emit();
        } catch (error) {
            console.error(error);
        } finally {
            this.creatingSveaPaymentLink = false;
        }
    }

    public async pushOrderToPurchOrder() {
        if (!this.get || this.pushingOrder) return;

        try {
            this.pushingOrder = true;

            const response = await this.orderhead.pushOrderToPurchaseOrder({ order_internal_no: this.get.order_internal_no });

            if (this.hasError(response)) {
                await this.showWarning(response.errormessage);
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.pushingOrder = false;
        }
    }

    public async cancelOrder() {
        if (!this.get || this.cancelling) return;

        try {
            this.cancelling = true;

            const cancellationCauses = await this.orderhead.getOrderlineCancellationCauses();
            const dialogRef = this.dialog.open<OrderHeadCancelDialogComponent, { cancellationCauses: typeof cancellationCauses }, OrderHeadCancelDialogResult | undefined>(OrderHeadCancelDialogComponent, {
                data: { cancellationCauses },
            });
            const result = await firstValueFrom(dialogRef.afterClosed());

            if (!result) return;

            const response = await this.orderhead.cancelOrderHead({
                order_internal_no: this.get.order_internal_no,
                orderlinecancelcause_keyno: result.orderlinecancelcause_keyno,
                cancellation_note: result.cancellation_note,
            });

            if (this.hasError(response)) {
                await this.showWarning(response.errormessage);
                return;
            }

            this.refreshRequested.emit();
        } catch (error) {
            console.error(error);
        } finally {
            this.cancelling = false;
        }
    }

    public async deleteOrder() {
        if (!this.get || this.deleting) return;

        try {
            this.deleting = true;

            const confirmed = await this.modal.openConfirmDialog({
                type: 'danger',
                title: 'confirm_title',
                translateTitle: true,
                message: 'confirm_message',
                translateMessage: true,
                ok: 'confirm_ok_label',
                cancel: 'confirm_cancel_label',
            });

            if (!confirmed) return;

            const response = await this.orderhead.deleteOrderHead({ order_internal_no: this.get.order_internal_no });

            if (this.hasError(response)) {
                await this.showWarning(response.errormessage);
                return;
            }

            this.state.back();
        } catch (error) {
            console.error(error);
        } finally {
            this.deleting = false;
        }
    }

    private hasError(response?: ErrorMessageResponse | ScanConsignmentResponse) {
        return '' + response?.errorcode !== '0' && response?.errorcode !== undefined;
    }

    private showWarning(message: string) {
        return this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message });
    }

    private goTo(response: ScanConsignmentResponse, event?: MouseEvent) {
        if (!response.webpagename) return;

        const params = response.parms as GotoParms<any>;

        if (!!event && this.utility.isModifierKeyPressed(event)) {
            this.state.newTab(response.webpagename, params);
        } else {
            this.state.go(response.webpagename, params);
        }
    }
}
